import { Outlet } from 'react-router-dom'
import { Suspense } from 'react'
import { AppLanguageSwitcher } from '@/components/ui/AppLanguageSwitcher'
import { ShopQuayWordmark } from '@/components/brand/ShopQuayWordmark'
import { useTranslation } from '@/i18n/useTranslation'
import { useShopAuthBranding, type ShopAuthBranding } from '@/hooks/useShopAuthBranding'
import LoadingSpinner from '@/components/ui/LoadingSpinner'

export type AuthOutletContext = {
  shopBranding: ShopAuthBranding
}

const AuthLayout = () => {
  const { t } = useTranslation('common')
  const shopBranding = useShopAuthBranding()
  const { config, primary, tokenStyle, loading, slug } = shopBranding
  const shopThemed = Boolean(slug)
  /** Platform SaaS entry (no tenant ?shop=) — ShopQuay chrome, not generic OMS. */
  const platformThemed = !shopThemed

  const pageStyle = shopThemed
    ? {
        ...tokenStyle,
        backgroundColor:
          tokenStyle.backgroundColor ||
          (primary ? `color-mix(in srgb, ${primary} 8%, white)` : undefined),
      }
    : undefined

  return (
    <div
      className={`relative min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8 ${
        shopThemed
          ? 'shop-storefront-root'
          : 'marketing-surface bg-[var(--mq-sky)]'
      }`}
      style={pageStyle}
    >
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <div className="text-center">
          {shopThemed && loading ? (
            <div className="flex justify-center py-4">
              <LoadingSpinner />
            </div>
          ) : config ? (
            <>
              {config.logo_url ? (
                <img
                  src={config.logo_url}
                  alt={config.name}
                  className="mx-auto h-14 w-14 rounded-xl object-contain"
                />
              ) : null}
              <h1
                className="mt-3 text-3xl font-bold tracking-tight"
                style={{ color: primary, fontFamily: 'var(--shop-heading-font, inherit)' }}
              >
                {config.name}
              </h1>
              <p className="mt-2 text-sm text-slate-600">{t('auth.shopLoginSubtitle')}</p>
            </>
          ) : platformThemed ? (
            <>
              <div className="flex justify-center">
                <ShopQuayWordmark
                  to="/"
                  variant="light"
                  heightClassName="h-12 sm:h-14"
                />
              </div>
              <p className="mt-3 text-sm text-[var(--mq-ink)]/60">{t('auth.platformLoginSubtitle')}</p>
            </>
          ) : (
            <>
              <h1 className="text-3xl font-bold text-gray-900">{t('appName')}</h1>
              <p className="mt-2 text-sm text-gray-600">{t('auth.appSubtitle')}</p>
            </>
          )}
          <div className="mt-4 flex justify-center">
            <AppLanguageSwitcher showLabel />
          </div>
        </div>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div
          className={`bg-white py-8 px-4 shadow sm:px-10 ${
            platformThemed
              ? 'border border-[var(--mq-ink)]/8 sm:rounded-2xl shadow-[var(--mq-ink)]/8'
              : 'sm:rounded-xl'
          }`}
        >
          <Suspense
            fallback={
              <div className="flex min-h-[12rem] items-center justify-center">
                <LoadingSpinner />
              </div>
            }
          >
            <Outlet context={{ shopBranding } satisfies AuthOutletContext} />
          </Suspense>
        </div>
      </div>

      <div className="mt-8 text-center">
        <p
          className={`text-xs ${
            platformThemed ? 'text-[var(--mq-ink)]/45' : 'text-gray-500'
          }`}
        >
          {platformThemed ? t('auth.platformCopyright') : t('auth.copyright')}
        </p>
      </div>
    </div>
  )
}

export default AuthLayout
